import { useTimerStore } from "@/stores/TimerStore";
import { useWaveStore } from "@/stores/WaveStore";
import { Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Poetsen_One } from "next/font/google";
import { useEffect, useState } from "react";
import TimerBackground from "./background/TimerBackground";
import ReplayIcon from '@mui/icons-material/Replay';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

export const poetsen_one = Poetsen_One({
  subsets: ['latin'],
  weight: ['400'],
});

function TimerComponent() {
  const timerStore = useTimerStore()
  const waveStore = useWaveStore()
  const timeLeftValue = useTimerStore(state => state.timeLeft)

  const [hydrated, setHydrated] = useState<boolean>(false);
  useEffect(() => {
    setHydrated(true);
  }, []);


  // Update wave based on timer progress
  useEffect(() => {
    if (timerStore.status === 'idle') {
      waveStore.resetWave()
    } else {
      // Use the new function that properly maps timer progress to wave height
      const duration = timerStore.getCurrentDuration()

      waveStore.updateWaveByTimerProgress(timerStore.timeLeft, duration)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeftValue])

  // Update color theme based on timer mode
  useEffect(() => {
    if (timerStore.mode === 'focus') {
      waveStore.setColorIndex(0) // Focus color
    } else if (timerStore.mode === 'shortBreak') {
      waveStore.setColorIndex(1) // Short break color
    } else if (timerStore.mode === 'longBreak') {
      waveStore.setColorIndex(2) // Long break color
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerStore.mode])

  const formatTime = () => {
    const { minutes, seconds } = timerStore.getDisplayTime()
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  return <>
    <motion.div
      className="w-full h-full bg-gradient-to-b from-navy-950 to-navy-900 flex flex-col items-center justify-between relative overflow-hidden"
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Timer section */}
      <div className="flex flex-col items-center justify-center flex-1 px-4">
        <motion.div
          className={`text-white text-7xl font-extralight tracking-wider ${poetsen_one.className}`}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30, delay: 0.3 }}
        >
          {hydrated && formatTime()}
        </motion.div>
      </div>

      {/* Bottom wave and timer information */}

    </motion.div>
  </>
}

export function PIPTimer() {
  return <>
    <div>
      {/* Outer square container holds the corner buttons */}
      <div className="w-70 h-70 relative">
        <div className="w-full">
          <PIPButtonFunctions />
        </div>
        {/* Inner circular timer UI */}
        <div className="w-full h-full shadow-lg rounded-full overflow-hidden flex flex-col">
          <TimerBackground>
            <div className="w-full h-full flex flex-col items-center justify-center">
              <TimerComponent />
            </div>
          </TimerBackground>
        </div>
      </div>
    </div>
  </>;
}

export default function Timer() {
  return <>
    <div>
      <div className="w-70 h-70 shadow-lg rounded-full overflow-hidden relative flex flex-col">
        <TimerBackground>
          <div className="w-full h-full flex flex-col items-center justify-center">
            <TimerComponent />
          </div>
        </TimerBackground>
      </div>
      <div className="w-70 flex justify-center items-center mt-8">
        <ButtonFunctions />
      </div>
    </div>
  </>;
}

const MotionButton = motion(Button);

function PIPButtonFunctions() {
  const timerStore = useTimerStore()

  const startPauseButtonColor = timerStore.status === 'running'
    ? 'var(--color-timer-button-pause)'
    : 'var(--color-timer-button-start)';

  const startPauseButtonHoverColor = timerStore.status === 'running'
    ? 'var(--color-timer-button-pause-hover)'
    : 'var(--color-timer-button-start-hover)';

  return <>
    {/* Start/Pause in top-left */}
    <MotionButton
      layout
      variant="contained"
      sx={{
        borderRadius: '50%',
        minWidth: '32px',
        width: '32px',
        height: '32px',
        boxShadow: 'none',
      }}
      animate={{
        backgroundColor: startPauseButtonColor
      }}
      whileHover={{
        backgroundColor: startPauseButtonHoverColor
      }}
      transition={{
        duration: 0.2,
        layout: {
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }
      }}
      className="backdrop-blur-md shadow-lg absolute top-2 left-2 z-10"
      onClick={
        timerStore.status === 'idle' || timerStore.status === 'paused' ? timerStore.startTimer : timerStore.pauseTimer
      }
    >
      {(timerStore.status === 'idle' || timerStore.status === 'paused') ? <PlayArrowIcon /> : <StopIcon />}
    </MotionButton>
    {/* Reset in top-right */}
    <AnimatePresence mode="popLayout">
      {!(timerStore.status === 'idle') && (
        <MotionButton
          key="reset-button"
          variant="contained"
          sx={{
            borderRadius: '50%',
            minWidth: '32px',
            width: '32px',
            height: '32px',
            backgroundColor: 'var(--color-timer-button-reset)',
            boxShadow: 'none',
            right: '2px',
            position: 'absolute',
          }}
          whileHover={{
            backgroundColor: 'var(--color-timer-button-reset-hover)',
          }}
          className="backdrop-blur-md shadow-lg absolute top-2 z-10"
          onClick={timerStore.resetTimer}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.5 }}
          transition={{ duration: 0.2 }}
        >
          <ReplayIcon />
        </MotionButton>
      )}
    </AnimatePresence>
  </>
}

function ButtonFunctions() {
  const timerStore = useTimerStore()

  const startPauseButtonColor = timerStore.status === 'running'
    ? 'var(--color-timer-button-pause)'
    : 'var(--color-timer-button-start)';

  const startPauseButtonHoverColor = timerStore.status === 'running'
    ? 'var(--color-timer-button-pause-hover)'
    : 'var(--color-timer-button-start-hover)';

  return <>
    <div className="pt-8" />
    <div className="gap-x-8 flex flex-row items-center justify-center h-16">
      <MotionButton
        layout
        variant="contained"
        sx={{
          borderRadius: '50%',
          minWidth: '64px',
          width: '64px',
          height: '64px',
          boxShadow: 'none',
        }}
        animate={{
          backgroundColor: startPauseButtonColor
        }}
        whileHover={{
          backgroundColor: startPauseButtonHoverColor
        }}
        transition={{
          duration: 0.2,
          layout: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }
        }}
        className="backdrop-blur-md shadow-lg"
        onClick={
          timerStore.status === 'idle' || timerStore.status === 'paused' ? timerStore.startTimer : timerStore.pauseTimer
        }
      >
        {(timerStore.status === 'idle' || timerStore.status === 'paused') ? <PlayArrowIcon /> : <StopIcon />}
      </MotionButton>
      <AnimatePresence mode="popLayout">
        {!(timerStore.status === 'idle') && (
          <MotionButton
            key="reset-button"
            variant="contained"
            sx={{
              borderRadius: '50%',
              minWidth: '64px',
              width: '64px',
              height: '64px',
              backgroundColor: 'var(--color-timer-button-reset)',
              boxShadow: 'none',
            }}
            whileHover={{
              backgroundColor: 'var(--color-timer-button-reset-hover)',
            }}
            className="backdrop-blur-md shadow-lg"
            onClick={timerStore.resetTimer}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.2 }}
          >
            <ReplayIcon />
          </MotionButton>
        )}
      </AnimatePresence>
    </div>
  </>
}